import React, { useState } from "react";
import { X, CheckCircle, Edit } from "lucide-react";
import { Modal } from "antd";
import AddExamModal from "../teacher/AddExamModal";

const ExamViewerEditorModal = ({ exam, onClose, onEdit }) => {
  const [isEditMode, setIsEditMode] = useState(false);

  // Handle exam update
  const handleExamUpdate = (updatedExam) => {
    onEdit(updatedExam);
    setIsEditMode(false);
  };

  if (isEditMode) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsEditMode(false)}
          className="absolute right-0 top-0 bg-gray-200 p-2 rounded-full hover:bg-gray-300"
        >
          <X size={18} />
        </button>
        <div className="mt-8">
          <AddExamModal
            onRequestClose={() => setIsEditMode(false)}
            setExamData={handleExamUpdate}
            currentExamData={exam}
            isCourse={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-[#241763]">{exam.title}</h3>
        <div className="flex gap-2"></div>
      </div>

      {exam.description && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2">Description</h4>
          <p className="text-gray-700 bg-gray-50 p-4 rounded">
            {exam.description}
          </p>
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center gap-2 text-gray-600 mb-4">
          <span className="font-medium">Exam Type:</span>
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
            {exam.type === "MCQ"
              ? "Multiple Choice Questions"
              : "Question & Answers"}
          </span>
        </div>
      </div>

      {exam.type === "MCQ" && exam.mcqs && exam.mcqs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 flex items-center">
            <span>Multiple Choice Questions</span>
            <span className="ml-2 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm">
              {exam.mcqs.length} questions
            </span>
          </h4>

          <div className="space-y-6">
            {exam.mcqs.map((mcq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
              >
                <div className="flex justify-between">
                  <h5 className="font-medium text-lg mb-3">
                    Question {index + 1}
                  </h5>
                  <div className="flex gap-2 text-sm">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                      Marks: {mcq.marks}
                    </span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                      Time: {mcq.time} sec
                    </span>
                  </div>
                </div>

                <p className="mb-4 text-gray-800">{mcq.question}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    className={`p-3 rounded ${
                      mcq.correctOption === "A"
                        ? "bg-green-100 border-green-300 border"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      {mcq.correctOption === "A" && (
                        <CheckCircle
                          size={16}
                          className="text-green-600 mr-2"
                        />
                      )}
                      <span className="font-medium mr-2">A:</span>
                      <span>{mcq.optionA}</span>
                    </div>
                  </div>
                  <div
                    className={`p-3 rounded ${
                      mcq.correctOption === "B"
                        ? "bg-green-100 border-green-300 border"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      {mcq.correctOption === "B" && (
                        <CheckCircle
                          size={16}
                          className="text-green-600 mr-2"
                        />
                      )}
                      <span className="font-medium mr-2">B:</span>
                      <span>{mcq.optionB}</span>
                    </div>
                  </div>
                  <div
                    className={`p-3 rounded ${
                      mcq.correctOption === "C"
                        ? "bg-green-100 border-green-300 border"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      {mcq.correctOption === "C" && (
                        <CheckCircle
                          size={16}
                          className="text-green-600 mr-2"
                        />
                      )}
                      <span className="font-medium mr-2">C:</span>
                      <span>{mcq.optionC}</span>
                    </div>
                  </div>
                  <div
                    className={`p-3 rounded ${
                      mcq.correctOption === "D"
                        ? "bg-green-100 border-green-300 border"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      {mcq.correctOption === "D" && (
                        <CheckCircle
                          size={16}
                          className="text-green-600 mr-2"
                        />
                      )}
                      <span className="font-medium mr-2">D:</span>
                      <span>{mcq.optionD}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {exam.type === "QA" &&
        exam.questionAnswers &&
        exam.questionAnswers.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-3 flex items-center">
              <span>Question & Answers</span>
              <span className="ml-2 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm">
                {exam.questionAnswers.length} questions
              </span>
            </h4>

            <div className="space-y-6">
              {exam.questionAnswers.map((qa, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
                >
                  <div className="flex justify-between">
                    <h5 className="font-medium text-lg mb-3">
                      Question {index + 1}
                    </h5>
                    <div className="flex gap-2 text-sm">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        Marks: {qa.marks}
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                        Time: {qa.time} sec
                      </span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="font-medium mb-1">Question:</p>
                    <p className="pl-4 text-gray-800">{qa.question}</p>
                  </div>

                  <div>
                    <p className="font-medium mb-1">Answer:</p>
                    <p className="pl-4 text-gray-800 bg-gray-50 p-3 rounded">
                      {qa.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      <div className="mt-4 text-right">
        <button
          onClick={onClose}
          className="bg-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-400"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ExamViewerEditorModal;
